import React, { useState, useEffect, useMemo, createContext, useContext, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Tooltip, TextField, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { FaArrowLeft, FaList, FaThLarge, FaThList } from 'react-icons/fa';
import { useAlert } from '../../../../../contexts/AlertContext';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  MainSection
} from '../../../../common/styles';
import { FormContainer } from '../../styles';
import { DirtyStateContext } from './components/ReviewerCompetencyList/CommentsField';
import StartAssignmentStep from './components/StartAssignmentStep';
import SummaryStep from './components/SummaryStep';
import SkillAssessmentStep from './components/SkillAssessmentStep';
import StepNavigation from './components/StepNavigation';
import ActionsStep from './components/ActionsStep';
import EmptyState from '../../../../common/EmptyState';
import { REVIEW_TYPES } from '../../../../../constants/reviewTypes';
import { getTemplateTypeText, getTemplateTypeConstant, MATRIX_TEMPLATE_TYPES } from '../../../../../constants/matrixTemplateTypes';
import { getDefaultLevelId } from '../../../../../constants/skillLevels';

// Custom hooks
import useAssignmentDetails from '../../../../../hooks/useAssignmentDetails';
import useSkillLevelManagement from '../../../../../hooks/useSkillLevelManagement';
import useCommentsManagement from '../../../../../hooks/useCommentsManagement';
import useAssignmentStateMachine from '../../../../../hooks/useAssignmentStateMachine';
import useFeedbackActionsManagement from '../../../../../hooks/useFeedbackActionsManagement';

const AssignmentDetails = () => {
  const { assignmentId, employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();

  // Refs
  const contentPanelRef = useRef(null);

  // Get scroll state and return path from navigation state
  const scrollState = location.state?.scrollState;
  const returnPath = location.state?.returnPath;

  // Handle back navigation with scroll state preservation
  const handleBack = useCallback(() => {
    if (returnPath && scrollState) {
      // Navigate back to the list with scroll state
      navigate(returnPath, {
        state: {
          fromDetail: true,
          scrollState,
          needsRestore: true,
          hideContentUntilScrolled: true,
          timestamp: Date.now()
        }
      });
    } else {
      // Fallback to browser back
      navigate(-1);
    }
  }, [navigate, returnPath, scrollState]);

  // UI state
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMapping, setActiveMapping] = useState(null);
  const [overallCompetencyAnchor, setOverallCompetencyAnchor] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [visualizationType, setVisualizationType] = useState('radio');
  const [isDirty, setIsDirty] = useState(false);
  const [savingFieldId, setSavingFieldId] = useState(null);
  const [submittingActions, setSubmittingActions] = useState(false);

  // Custom hooks
  const {
    assignment,
    setAssignment,
    loading,
    competencies,
    setCompetencies,
    currentSkillTemplate,
    loadingSkillTemplate,
    skillLevels,
    defaultLevelId,
    myReviewer,
    isSelfAssessment,
    isParallelReview,
    isAssessmentSubmitted,
    isAssignmentCompleted,
    isSessionLocked,
    fetchSkillTemplateDetails,
    triggerRefresh,
    getSkillLevelsBySegmentId,
    getCustomDescriptionsBySegmentId,
    getCustomDescriptionsBySkill
  } = useAssignmentDetails(assignmentId, employeeId);

  const {
    loadingSegmentMap,
    loadingOverallSkill,
    getSegmentLevel,
    getOverallLevel,
    handleSegmentLevelSelect,
    handleOverallLevelSelect
  } = useSkillLevelManagement(competencies, setCompetencies, myReviewer, triggerRefresh, defaultLevelId);

  const {
    handleCommentChange,
    handleSegmentCommentChange
  } = useCommentsManagement(competencies, setCompetencies, myReviewer, employeeId);

  // Find the current reviewer from assignment.reviewers for feedback actions
  const currentReviewerForActions = assignment?.reviewers?.find(reviewer => 
    reviewer.employee.id === employeeId && reviewer.id === myReviewer?.id
  ) || myReviewer;

  const {
    handleFeedbackActionsChange
  } = useFeedbackActionsManagement(assignment, setAssignment, myReviewer, employeeId);

  const {
    activeStep,
    setActiveStep,
    startingAssignment,
    submitting,
    isStepComplete,
    isAllPreviousStepsComplete,
    areFeedbackActionsValid,
    handleStep,
    handleStartAssignment,
    handleSubmitAssignment,
    initializeStep
  } = useAssignmentStateMachine(
    assignment,
    myReviewer,
    competencies,
    defaultLevelId,
    getOverallLevel,
    getSegmentLevel,
    triggerRefresh
  );

  // Initialize step when assignment data is loaded
  useEffect(() => {
    initializeStep();
  }, [myReviewer?.startedAt, activeStep]);

  // Fetch skill template details when active step changes
  useEffect(() => {
    fetchSkillTemplateDetails(activeStep);
  }, [activeStep, myReviewer, fetchSkillTemplateDetails]);

  // Scroll content panel to top when step changes
  useEffect(() => {
    if (contentPanelRef.current) {
      contentPanelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeStep]);

  // Title and message helpers
  const getAssignmentTitle = () => {
    if (!assignment || !myReviewer) return '';
    
    const reviewerName = myReviewer.employee.fullName;
    const employeeName = assignment.employee.fullName;
    const templateTypeName = getTemplateTypeText(myReviewer.skillsMatrixTemplate.type);
    const reviewerOrder = myReviewer.order;
    const templateType = getTemplateTypeConstant(myReviewer.skillsMatrixTemplate.type);

    if (isParallelReview()) {
      if (templateType === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT) {
        return `Self Assessment of ${employeeName}`;
      }
      return `${templateTypeName} | ${reviewerName} for ${employeeName}`;
    } else if (isSelfAssessment()) {
      return `Self Assessment of ${employeeName}`;
    } else {
      return `Reviewer ${reviewerOrder}, ${reviewerName} for ${employeeName}`;
    }
  };

  const getStartAssignmentMessage = () => {
    if (!assignment || !myReviewer) return '';
    
    const employeeName = assignment.employee.fullName;
    const templateType = getTemplateTypeConstant(myReviewer.skillsMatrixTemplate.type);

    if (isParallelReview()) {
      if (templateType === MATRIX_TEMPLATE_TYPES.SELF_ASSESSMENT) {
        return `You are about to start your skills matrix assessment. Click "Start" to begin evaluating your skills.`;
      }
      return `You are about to start evaluating ${employeeName}'s skills matrix. Click "Start" to begin the assessment.`;
    } else if (isSelfAssessment()) {
      return `You are about to start your skills matrix assessment. Click "Start" to begin evaluating your skills.`;
    } else {
      return `You are about to start reviewing ${employeeName}'s skills matrix. Click "Start" to begin the assessment.`;
    }
  };

  // UI event handlers
  const handleOpenSelector = (event, reviewer, segmentId) => {
    if (isDirty) return; // Don't allow opening selectors while saving
    
    // Find the mapping for this segment
    const mapping = myReviewer?.skillsMatrixTemplate?.skillsTemplates[activeStep - 1].mappings.find(
      m => m.segment.id === segmentId
    );
    
    if (mapping) {
      setAnchorEl(event.currentTarget);
      setActiveMapping(mapping);
    } else if (!segmentId) {
      // This is for overall competency
      setOverallCompetencyAnchor(event.currentTarget);
    }
  };

  const handleCloseSelector = () => {
    setAnchorEl(null);
    setActiveMapping(null);
  };

  const handleLevelSelect = (reviewer, skillId, segmentId, level) => {
    // Find the mapping for this segment
    const mapping = myReviewer?.skillsMatrixTemplate?.skillsTemplates[activeStep - 1].mappings.find(
      m => m.segment.id === segmentId
    );

    if (mapping) {
      // Close all selectors immediately
      handleCloseSelector();
      setOverallCompetencyAnchor(null);
      // Get segment-specific default level for optional skill comment clearing
      const segmentSkillLevels = getSkillLevelsBySegmentId(segmentId);
      const segmentDefaultLevelId = getDefaultLevelId(segmentSkillLevels);
      // Call the hook function with segment's default level
      handleSegmentLevelSelect(mapping, activeStep, segmentDefaultLevelId)(level);
    }
  };

  // Override the overall level select to include closing logic
  const handleOverallLevelSelectWithClose = async (reviewer, skillId, segmentId, levelId) => {
    // Close the selector immediately
    setOverallCompetencyAnchor(null);
    // Get the current skill template to check if it's optional
    const skillTemplate = myReviewer?.skillsMatrixTemplate?.skillsTemplates?.[activeStep - 1];
    // Call the hook function with skill template for optional skill handling
    await handleOverallLevelSelect(reviewer, skillId, segmentId, levelId, skillTemplate);
  };

  // Override the segment level select to include closing logic
  const handleSegmentLevelSelectWithClose = (mapping, activeStep) => async (levelId) => {
    // Close the selector immediately
    handleCloseSelector();
    // Get segment-specific default level for optional skill comment clearing
    const segmentSkillLevels = getSkillLevelsBySegmentId(mapping.segment.id);
    const segmentDefaultLevelId = getDefaultLevelId(segmentSkillLevels);
    // Call the hook function with segment's default level
    await handleSegmentLevelSelect(mapping, activeStep, segmentDefaultLevelId)(levelId);
  };

  const handleVisualizationChange = (event, newValue) => {
    if (isDirty || newValue === null) return; // Don't allow changing visualization while saving
    setVisualizationType(newValue);
  };

  const handleSubmitWithConfirmation = async () => {
    setConfirmDialogOpen(false);
    const success = await handleSubmitAssignment(isDirty);
    if (success) {
      // Check if we should navigate to feedback actions step instead of leaving
      const shouldShowActions = myReviewer?.reviewType === REVIEW_TYPES.SEQUENTIAL &&
                                myReviewer?.order !== 1;

      if (shouldShowActions) {
        // Refresh to get updated completedAt, then navigate to actions step
        triggerRefresh();
        const actionsStepIndex = (myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length || 0) + 2;
        setActiveStep(actionsStepIndex);
      } else {
        handleBack(); // Go back to assignments list
      }
    }
  };

  const handleSaveActions = async (actionType, comments) => {
    try {
      await handleFeedbackActionsChange(actionType, comments);
      // No refresh - let the API save work like comments do
    } catch (error) {
      throw error;
    }
  };

  // Helper function to determine if ActionsStep should be shown
  const shouldShowActionsStep = () => {
    if (!myReviewer || !assignment) return false;
    
    const isSequentialReview = myReviewer.reviewType === REVIEW_TYPES.SEQUENTIAL;
    const isNotSelfAssessment = myReviewer.order !== 1;
    const hasCompletedAssignment = myReviewer.completedAt;
    
    return isSequentialReview && isNotSelfAssessment && hasCompletedAssignment;
  };

  // Handle loading and error states
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (!assignment) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={handleBack}>
              <FaArrowLeft /> Back to Assignments
            </BackButton>
            <Title>Skills Matrix Assignment</Title>
          </HeaderLeft>
        </Header>
        <Content>
          <MainSection>
            <FormContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <EmptyState
                  title="Assignment not found"
                  description="The requested skills matrix assignment could not be found. Please check the URL and try again."
                />
              </Box>
            </FormContainer>
          </MainSection>
        </Content>
      </Container>
    );
  }

  return (
    <DirtyStateContext.Provider value={{ isDirty, setIsDirty, savingFieldId, setSavingFieldId }}>
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={() => !isDirty && handleBack()}>
              <FaArrowLeft /> Back to Assignments
            </BackButton>
            <Title>Skills Matrix Assignment | {getAssignmentTitle()}</Title>
          </HeaderLeft>
          {myReviewer?.startedAt && (
            <Box>
              <Tooltip title="Toggle visualization type">
                <ToggleButtonGroup
                  value={visualizationType}
                  exclusive
                  onChange={handleVisualizationChange}
                  size="small"
                  aria-label="visualization type"
                  disabled={isDirty}
                >
                  <ToggleButton value="dropdown" aria-label="dropdown view">
                    <FaList />
                  </ToggleButton>
                  <ToggleButton value="radio" aria-label="radio view">
                    <FaThLarge />
                  </ToggleButton>
                  <ToggleButton value="radio-limited" aria-label="radio limited view">
                    <FaThList />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            </Box>
          )}
        </Header>

        <Content style={{ marginTop: '-8px' }}>
          <Box sx={{
            display: 'flex',
            gap: 2,
            height: '100%',
            overflow: 'hidden'
          }}>
            {/* Left stepper panel */}
            <Box sx={{
              width: 360,
              flexShrink: 0,
              overflowY: 'auto',
              bgcolor: 'white',
              borderRadius: '0 0 0 8px',
              p: 2,
            }}>
              <StepNavigation
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                myReviewer={myReviewer}
                isDirty={isDirty}
                isStepComplete={isStepComplete}
                handleStep={handleStep}
                startAssignmentMessage={getStartAssignmentMessage()}
                getSegmentLevel={getSegmentLevel}
                getOverallLevel={getOverallLevel}
                defaultLevelId={defaultLevelId}
                skillLevels={skillLevels}
                shouldShowActionsStep={shouldShowActionsStep()}
              />
            </Box>

            {/* Main content panel */}
            <Box
              ref={contentPanelRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                bgcolor: 'white',
                borderRadius: '0 0 8px 0',
                p: 3,
              }}
            >
              {myReviewer?.skillsMatrixTemplate?.skillsTemplates?.[activeStep - 1] ? (
                <SkillAssessmentStep
                  activeStep={activeStep}
                  myReviewer={myReviewer}
                  sessionLocked={isSessionLocked()}
                  currentSkillTemplate={currentSkillTemplate}
                  loadingSkillTemplate={loadingSkillTemplate}
                  skillLevels={skillLevels}
                  defaultLevelId={defaultLevelId}
                  assignment={assignment}
                  competencies={competencies}
                  employeeId={employeeId}
                  visualizationType={visualizationType}
                  handleOverallLevelSelectWithClose={handleOverallLevelSelectWithClose}
                  handleOpenSelector={handleOpenSelector}
                  handleCommentChange={handleCommentChange}
                  loadingSegmentMap={loadingSegmentMap}
                  loadingOverallSkill={loadingOverallSkill}
                  followUpQuestions={currentSkillTemplate?.skill?.followUpQuestions || []}
                  handleLevelSelect={handleLevelSelect}
                  handleSegmentCommentChange={handleSegmentCommentChange}
                  anchorEl={anchorEl}
                  handleCloseSelector={handleCloseSelector}
                  activeMapping={activeMapping}
                  getSegmentLevel={getSegmentLevel}
                  handleSegmentLevelSelectWithClose={handleSegmentLevelSelectWithClose}
                  overallCompetencyAnchor={overallCompetencyAnchor}
                  setOverallCompetencyAnchor={setOverallCompetencyAnchor}
                  getOverallLevel={getOverallLevel}
                  isDirty={isDirty}
                  handleStep={handleStep}
                  isStepComplete={isStepComplete}
                  getSkillLevelsBySegmentId={getSkillLevelsBySegmentId}
                  getCustomDescriptionsBySegmentId={getCustomDescriptionsBySegmentId}
                  getCustomDescriptionsBySkill={getCustomDescriptionsBySkill}
                />
              ) : activeStep === 0 ? (
                <StartAssignmentStep
                  startMessage={getStartAssignmentMessage()}
                  isAssignmentCompleted={isAssignmentCompleted()}
                  sessionLocked={isSessionLocked()}
                  isDirty={isDirty}
                  startingAssignment={startingAssignment}
                  handleStartAssignment={handleStartAssignment}
                />
              ) : activeStep === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 1 ? (
                <SummaryStep
                  activeStep={activeStep}
                  assignment={assignment}
                  myReviewer={myReviewer}
                  competencies={competencies}
                  employeeId={employeeId}
                  visualizationType={visualizationType}
                  currentSkillTemplate={currentSkillTemplate}
                  loadingSkillTemplate={loadingSkillTemplate}
                  skillLevels={skillLevels}
                  loadingSegmentMap={loadingSegmentMap}
                  loadingOverallSkill={loadingOverallSkill}
                  isAllPreviousStepsComplete={isAllPreviousStepsComplete()}
                  isAssessmentSubmitted={isAssessmentSubmitted()}
                  sessionLocked={isSessionLocked()}
                  isDirty={isDirty}
                  submitting={submitting}
                  confirmDialogOpen={confirmDialogOpen}
                  setConfirmDialogOpen={setConfirmDialogOpen}
                  handleStep={handleStep}
                  handleSubmitWithConfirmation={handleSubmitWithConfirmation}
                  getCustomDescriptionsBySegmentId={getCustomDescriptionsBySegmentId}
                />
              ) : shouldShowActionsStep() && activeStep === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 2 ? (
                <ActionsStep
                  activeStep={activeStep}
                  assignment={assignment}
                  myReviewer={myReviewer}
                  employeeId={employeeId}
                  isDirty={isDirty}
                  onSaveActions={handleSaveActions}
                  submittingActions={submittingActions}
                  confirmDialogOpen={confirmDialogOpen}
                  setConfirmDialogOpen={setConfirmDialogOpen}
                  handleSubmitWithConfirmation={handleSubmitWithConfirmation}
                  areFeedbackActionsValid={areFeedbackActionsValid}
                />
              ) : null}
            </Box>
          </Box>
        </Content>
      </Container>
    </DirtyStateContext.Provider>
  );
};

export default AssignmentDetails; 